import { getAllVideos, getPinsForVideo, calculateTastemakerScore } from './metadata';

export interface TagFeedItem {
  tag: string;
  videoHash: string;
  video: any;
  uploadedAt: number;
  pinCount: number;
}

export function getAllTags(): string[] {
  const videos = getAllVideos();
  const tags = new Set<string>();
  
  videos.forEach(video => {
    video.tags?.forEach(tag => tags.add(tag));
  });
  
  return Array.from(tags).sort();
}

export function getVideosByTag(tag: string): any[] {
  const videos = getAllVideos();
  return videos.filter(v => v.tags?.includes(tag));
}

export function getTagFreshFeed(tag: string): TagFeedItem[] {
  const videos = getVideosByTag(tag);
  
  return videos
    .map(video => ({
      tag,
      videoHash: video.hash,
      video,
      uploadedAt: video.uploadedAt,
      pinCount: getPinsForVideo(video.hash).length
    }))
    .sort((a, b) => b.uploadedAt - a.uploadedAt)
    .slice(0, 50);
}

export function getTagPopularFeed(tag: string): TagFeedItem[] {
  const videos = getVideosByTag(tag);
  
  return videos
    .map(video => ({
      tag,
      videoHash: video.hash,
      video,
      uploadedAt: video.uploadedAt,
      pinCount: getPinsForVideo(video.hash).length
    }))
    .sort((a, b) => b.pinCount - a.pinCount)
    .slice(0, 50);
}

export function getTagRisingFeed(tag: string): TagFeedItem[] {
  const videos = getVideosByTag(tag);
  const now = Date.now();
  const last24h = now - (24 * 60 * 60 * 1000);

  return videos
    .map(video => {
      const pins = getPinsForVideo(video.hash);
      const recentPins = pins.filter(p => p.pinnedAt > last24h);
      
      let tastemakerWeight = 0;
      recentPins.forEach(pin => {
        const score = calculateTastemakerScore(pin.userId);
        tastemakerWeight += Math.log10(Math.max(score, 1)) * 10;
      });
      
      return {
        tag,
        videoHash: video.hash,
        video,
        uploadedAt: video.uploadedAt,
        pinCount: pins.length,
        momentum: recentPins.length,
        tastemakerMomentum: tastemakerWeight
      };
    })
    .filter(item => (item as any).momentum > 0)
    .sort((a, b) => (b as any).tastemakerMomentum - (a as any).tastemakerMomentum)
    .slice(0, 50);
}

export function getPopularTags(limit: number = 20): { tag: string; count: number }[] {
  const videos = getAllVideos();
  const tagCounts = new Map<string, number>();
  
  videos.forEach(video => {
    video.tags?.forEach(tag => {
      const pins = getPinsForVideo(video.hash).length;
      tagCounts.set(tag, (tagCounts.get(tag) || 0) + pins);
    });
  });
  
  return Array.from(tagCounts.entries())
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

export function getFreshTags(limit: number = 20): { tag: string; count: number }[] {
  const videos = getAllVideos();
  const now = Date.now();
  const last7days = now - (7 * 24 * 60 * 60 * 1000);
  
  const tagCounts = new Map<string, number>();
  
  videos
    .filter(v => v.uploadedAt > last7days)
    .forEach(video => {
      video.tags?.forEach(tag => {
        tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
      });
    });
  
  return Array.from(tagCounts.entries())
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

export function getRisingTags(limit: number = 20): { tag: string; momentum: number }[] {
  const videos = getAllVideos();
  const now = Date.now();
  const last24h = now - (24 * 60 * 60 * 1000);
  
  const tagMomentum = new Map<string, number>();
  
  videos.forEach(video => {
    const pins = getPinsForVideo(video.hash);
    const recentPins = pins.filter(p => p.pinnedAt > last24h);
    
    if (recentPins.length > 0) {
      let tastemakerWeight = 0;
      recentPins.forEach(pin => {
        const score = calculateTastemakerScore(pin.userId);
        tastemakerWeight += Math.log10(Math.max(score, 1)) * 10;
      });
      
      video.tags?.forEach(tag => {
        tagMomentum.set(tag, (tagMomentum.get(tag) || 0) + tastemakerWeight);
      });
    }
  });
  
  return Array.from(tagMomentum.entries())
    .map(([tag, momentum]) => ({ tag, momentum }))
    .sort((a, b) => b.momentum - a.momentum)
    .slice(0, limit);
}
